import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { APP_TIMEZONE } from '../config';
import type { ProjectTask, SkillItem } from '../types';

export interface WeekDay {
  date: string;        // YYYY-MM-DD
  isToday: boolean;
  isPast: boolean;
  tasks: ProjectTask[];
  checklistDone: number;
  checklistTotal: number;
}

export interface WeekData {
  days: WeekDay[];
  overdue: ProjectTask[];   // due before this week, not done — carry-over
  skills: SkillItem[];      // unscheduled backlog
  weekStart: string;
  weekEnd: string;
}

function denverToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: APP_TIMEZONE });
}

function ymd(dt: Date): string {
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

// Seven YYYY-MM-DD strings, Monday→Sunday, for the week containing `todayStr`.
function weekDayStrings(todayStr: string): string[] {
  const [y, m, d] = todayStr.split('-').map(Number);
  const base = new Date(y, m - 1, d);
  const mondayOffset = (base.getDay() + 6) % 7; // 0 = Monday
  const monday = new Date(base);
  monday.setDate(base.getDate() - mondayOffset);
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(monday);
    dt.setDate(monday.getDate() + i);
    return ymd(dt);
  });
}

const doneLast = (a: ProjectTask, b: ProjectTask) =>
  (a.status === 'done' ? 1 : 0) - (b.status === 'done' ? 1 : 0);

export function useWeek() {
  const { user } = useAuth();
  const [data, setData] = useState<WeekData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWeek = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const today = denverToday();
      const days = weekDayStrings(today);
      const weekStart = days[0];
      const weekEnd = days[6];
      const scope = `assignee_id.is.null,assignee_id.eq.${user.id}`;

      // Tasks due within the week (placed on their due day)
      const { data: weekTasks, error: tErr } = await supabase
        .from('intern_project_tasks_view')
        .select('*')
        .or(scope)
        .gte('due_date', weekStart)
        .lte('due_date', weekEnd);
      if (tErr) throw tErr;

      // Carry-over: due before this week and still open
      const { data: overdueTasks } = await supabase
        .from('intern_project_tasks_view')
        .select('*')
        .or(scope)
        .lt('due_date', weekStart)
        .neq('status', 'done')
        .order('due_date', { ascending: true })
        .limit(50);

      // Active checklist templates (recurring — same set every day)
      const { data: templates, error: cErr } = await supabase
        .from('intern_checklist_template_items')
        .select('id')
        .eq('active', true)
        .or(scope);
      if (cErr) throw cErr;
      const checklistTotal = (templates ?? []).length;

      // Checklist completion recorded across the week
      const { data: entries } = await supabase
        .from('intern_checklist_entries')
        .select('entry_date, checked')
        .eq('user_id', user.id)
        .gte('entry_date', weekStart)
        .lte('entry_date', weekEnd);
      const doneByDate = new Map<string, number>();
      for (const e of entries ?? []) {
        if (e.checked) doneByDate.set(e.entry_date, (doneByDate.get(e.entry_date) ?? 0) + 1);
      }

      // Skills backlog (scoped, not yet explored)
      const { data: skills } = await supabase
        .from('intern_skill_items')
        .select('*')
        .neq('status', 'explored')
        .or(scope)
        .order('sort_order', { ascending: true });

      const allWeekTasks = (weekTasks as ProjectTask[]) ?? [];
      const weekDays: WeekDay[] = days.map((date) => ({
        date,
        isToday: date === today,
        isPast: date < today,
        tasks: allWeekTasks.filter((t) => t.due_date === date).sort(doneLast),
        checklistDone: doneByDate.get(date) ?? 0,
        checklistTotal,
      }));

      setData({
        days: weekDays,
        overdue: (overdueTasks as ProjectTask[]) ?? [],
        skills: (skills as SkillItem[]) ?? [],
        weekStart,
        weekEnd,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load your week.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchWeek();
  }, [fetchWeek]);

  return { data, loading, error, refresh: fetchWeek };
}
